import { getAuth } from "firebase/auth";
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, writeBatch, limit } from "firebase/firestore";
import { firebase } from "../firebase-config";
import { COLLECTIONS } from "../config";

/** Merge spanish_* only when keys are present on source (Firestore-safe partial updates). */
function applyOptionalSpanishFirestoreFields(target, source) {
    if (!source || !target) return;
    if (Object.prototype.hasOwnProperty.call(source, "spanish_title")) {
        target.spanish_title = source.spanish_title ?? null;
    }
    if (Object.prototype.hasOwnProperty.call(source, "spanish_description")) {
        target.spanish_description = source.spanish_description ?? null;
    }
}

export async function saveSettings(formData, collectionName) {
    const auth = getAuth();
    try {
        const currentUser = auth.currentUser;
        const currentUserId = currentUser ? currentUser.uid : null;
        if (!currentUser) {
            throw new Error("User is not authenticated.");
        }
        for (const key in formData) {
            if (formData[key] === undefined) {
                formData[key] = "";
            }
        }
        
        const saveData = {
            ...formData,
            createdAt: serverTimestamp(),
            createdBy: currentUserId,
        }
        const docRef = await addDoc(collection(firebase, collectionName), saveData);
        return { status: true, id: docRef.id };
    } catch (error) {
        console.error("Error saving settings:", error);
        return { status: false, error: error.message };
    }
}


export async function updateSettings(formData, collectionName, docId) {
    const auth = getAuth();
    try {
        const currentUser = auth.currentUser;
        const currentUserId = currentUser ? currentUser.uid : null;

        if (!currentUser) {
            throw new Error("User is not authenticated.");
        }
        for (const key in formData) {
            if (formData[key] === undefined) {
                formData[key] = "";
            }
        }
        const saveData = {
            ...formData,
            updatedAt: serverTimestamp(),
            updatedBy: currentUserId,
        };
        const docRef = doc(firebase, collectionName, docId);
        const response = await setDoc(docRef, saveData, { merge: true });
        
        return { status: true, id: docRef.id };
    } catch (error) {
        console.error("Error saving settings:", error);
        return { status: false, error: error.message };
    }
}


export function subscribeToSettings(companyId, collectionName, onUpdate, onError, limitCount = 100) {
  try {
    const collectionRef = collection(firebase, collectionName);
    const q = query(
      collectionRef, 
      where("companyId", "==", companyId),
      limit(limitCount)
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        let results = [];
        querySnapshot.forEach((doc) => {
          results.push({ id: doc.id, ...doc.data() });
        });

        const filtered = results?.find(
          item => item.companyId === companyId && !item.deleted
        );

        onUpdate && onUpdate(filtered || null);
      },
      (error) => {
        console.log("Error subscribing to settings: ", error);
        onError && onError(error);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.log("Error setting up settings subscription: ", error);
    onError && onError(error);
  }
}


export async function getSettings(collectionName, companyId, onSuccess, onError) {
  try {
    const colRef = collection(firebase, collectionName);

    const q = companyId ? query(colRef, where("companyId", "==", companyId)) : colRef;

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      onSuccess && onSuccess({ status: true, data: [] });
      return;
    }

    const settings = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    onSuccess && onSuccess({ status: true, data: settings });
  } catch (error) {
    onError && onError({ status: false, error });
    onSuccess && onSuccess({ status: true, data: [] });
  }
}

export async function getInvoice(stripeCustomerId, onSuccess, onError) {
    try {
        const colRef = collection(firebase, COLLECTIONS.STRIPE_INVOICE);

        const querySnapshot =
          stripeCustomerId == null || stripeCustomerId === ""
            ? await getDocs(colRef)
            : await getDocs(query(colRef, where("customer", "==", stripeCustomerId)));

        if (querySnapshot.empty) {
            onSuccess && onSuccess({ status: true, data: [] });
        } else {
            const invoices = [];
            querySnapshot.forEach(doc => {
                invoices?.push({ id: doc.id, ...doc.data() });
            });
            onSuccess && onSuccess({ status: true, data: invoices });
        }
    } catch (error) {
        onError && onError({ status: false, error });
        onSuccess && onSuccess({ status: true, data: [] });
    }
}


export async function resetSettings(collectionName, docId) {
    const auth = getAuth();
    try {
        const currentUser = auth.currentUser;
        const currentUserId = currentUser ? currentUser.uid : null;

        if (!currentUser) {
            throw new Error("User is not authenticated.");
        }

        const saveData = {
            deleted: true,
            deletedAt: serverTimestamp(),
            deletedBy: currentUserId,
        };

        const docRef = doc(firebase, collectionName, docId);

        await setDoc(docRef, saveData, { merge: true });

        return { status: true, id: docRef.id };
    } catch (error) {
        console.error("Error performing soft delete:", error);
        return { status: false, error: error.message };
    }
}

export async function getSuppliesCompanyCategories(companyId, onSuccess, onError) {
    const colRef = collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY);
  
    try {
      const q = companyId
        ? query(colRef, where("companyId", "==", companyId))
        : colRef;
      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter((item) => !item.deleted);
      if (onSuccess) {
        onSuccess(results);
      }
    } catch (error) {
      console.log('Error getting documents: ', error);
      if (onError) {
        onError(error);
      }
    }
}

export async function saveOrUpdateSuppliesCompanyCategories(formDataArray) {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error("User is not authenticated.");
    }

    const currentUserId = currentUser.uid;
    const operations = formDataArray.map(async (formData) => {
        const docRef = doc(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY, formData.id);
        
        try {
            const docSnap = await getDoc(docRef);
            const { spanish_title, spanish_description, ...core } = formData;
            const saveData = {
                ...core,
                createdAt: serverTimestamp(),
                createdBy: currentUserId,
            };
            applyOptionalSpanishFirestoreFields(saveData, formData);

            if (docSnap.exists()) {
                await updateDoc(docRef, saveData);
                return { status: true, id: formData.id, message: `Document ${formData.id} updated.` };
            } else {
                await setDoc(docRef, saveData);
                return { status: true, id: formData.id, message: `New document ${formData.id} added.` };
            }
        } catch (error) {
            console.error(`Error processing document ${formData.id}:`, error);
            return { status: false, id: formData.id, error: error.message };
        }
    });

    const results = await Promise.all(operations);
    return { status: true, results };
}

export async function resetSuppliesCompanyCategory(companyId, onSuccess, onError) {
    try {
        const colRef = collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY);
        const q = query(colRef, where("companyId", "==", companyId));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            onError && onError({ status: false, error: "No Settings found" });
            return { status: false, error: "No Settings found" };
        }

        const updatePromises = [];
        const updatedSettings = [];

        querySnapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data();
            if (!data.deleted) {
                const docRef = doc(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY, docSnapshot.id);
                updatePromises.push(updateDoc(docRef, { deleted: true }));
                updatedSettings.push({ id: docSnapshot.id, ...data, deleted: true });
            }
        });

        await Promise.all(updatePromises);

        onSuccess && onSuccess({ status: true, data: updatedSettings });
        return { status: true, data: updatedSettings };

    } catch (error) {
        onError && onError({ status: false, error: error.message });
        return { status: false, error: error.message };
    }
}

export async function getProductDetails(id, onSuccess, onError) {
    try {
        const productDocRef = doc(firebase, COLLECTIONS.STRIPE_PRODUCTS, id);

        const docSnapshot = await getDoc(productDocRef);

        if (!docSnapshot.exists()) {
            onError && onError({ status: false, error: "Product not found" });
        } else {
            const data = docSnapshot.data();
            onSuccess && onSuccess({ status: true, data: { id: docSnapshot.id, ...data } });
        }
    } catch (error) {
        onError && onError({ status: false, error: error });
    }
}

/** Stripe price docs — cached so repeat visits avoid Firestore on every navigation. */
const STRIPE_PRODUCTS_SESSION_KEY = "pt_cms_stripe_products_v1";
/** Hot cache: instant return without network (same tab / SPA session). */
const STRIPE_PRODUCTS_MEMORY_TTL_MS = 30 * 60 * 1000;
/** Session cache: survive remounts; still refreshed when memory TTL expires. */
const STRIPE_PRODUCTS_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

let stripeProductsMemory = null;
let stripeProductsMemoryTs = 0;
let stripeProductsInflight = null;

function readStripeProductsSessionEntry(maxAgeMs = STRIPE_PRODUCTS_SESSION_MAX_AGE_MS) {
  try {
    const raw = sessionStorage.getItem(STRIPE_PRODUCTS_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.data)) return null;
    const ts = typeof parsed.ts === "number" ? parsed.ts : 0;
    if (Date.now() - ts > maxAgeMs) return null;
    return { data: parsed.data, ts };
  } catch {
    return null;
  }
}

function writeStripeProductsSession(data) {
  try {
    sessionStorage.setItem(
      STRIPE_PRODUCTS_SESSION_KEY,
      JSON.stringify({ ts: Date.now(), data })
    );
  } catch (e) {
    if (e?.name === "QuotaExceededError") {
      try {
        sessionStorage.removeItem(STRIPE_PRODUCTS_SESSION_KEY);
      } catch {
        /* ignore */
      }
    }
  }
}

/**
 * Synchronous read of last-known-good products (memory, then session).
 * Used to paint the Upgrade page immediately on repeat loads.
 */
export function peekCachedStripeProductsRaw() {
  const now = Date.now();
  if (Array.isArray(stripeProductsMemory) && stripeProductsMemory.length && now - stripeProductsMemoryTs < STRIPE_PRODUCTS_MEMORY_TTL_MS) {
    return stripeProductsMemory;
  }
  const session = readStripeProductsSessionEntry(STRIPE_PRODUCTS_MEMORY_TTL_MS);
  if (session?.data?.length) {
    stripeProductsMemory = session.data;
    stripeProductsMemoryTs = session.ts;
    return stripeProductsMemory;
  }
  return null;
}

/** Call after admin-side price changes or successful plan purchase if you need a hard refresh. */
export function invalidateStripeProductsCache() {
  stripeProductsMemory = null;
  stripeProductsMemoryTs = 0;
  stripeProductsInflight = null;
  try {
    sessionStorage.removeItem(STRIPE_PRODUCTS_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

async function fetchStripeProductsFromFirestoreOnce() {
  const productsRef = collection(firebase, COLLECTIONS.STRIPE_PRODUCTS);
  const mapDocs = (snapshot) =>
    snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));

  try {
    const activeQuery = query(productsRef, where("active", "==", true));
    let snapshot = await getDocs(activeQuery);

    if (snapshot.empty) {
      const allSnapshot = await getDocs(productsRef);
      if (!allSnapshot.empty) {
        return mapDocs(allSnapshot);
      }
      return [];
    }

    return mapDocs(snapshot);
  } catch (error) {
    console.error("Failed to fetch products (active query):", error);
    try {
      const snapshot = await getDocs(productsRef);
      return snapshot.empty ? [] : mapDocs(snapshot);
    } catch (fallbackError) {
      console.error("Failed to fetch products:", fallbackError);
      return [];
    }
  }
}

/**
 * Fetches Stripe price documents. Uses in-memory + sessionStorage cache with TTL
 * so navigating back to Plans does not hit Firestore every time. In-flight requests
 * are deduped. On network failure, returns last cached session data when available.
 *
 * @param {{ forceRefresh?: boolean }} [options]
 */
export async function getAllProducts(options = {}) {
  const force = options.forceRefresh === true;
  if (!force) {
    const hot = peekCachedStripeProductsRaw();
    if (hot) return hot;
    if (stripeProductsInflight) return stripeProductsInflight;
  } else {
    stripeProductsInflight = null;
  }

  const p = (async () => {
    try {
      const data = await fetchStripeProductsFromFirestoreOnce();
      const arr = Array.isArray(data) ? data : [];
      stripeProductsMemory = arr;
      stripeProductsMemoryTs = Date.now();
      if (arr.length) {
        writeStripeProductsSession(arr);
      }
      return arr;
    } catch (e) {
      console.error("getAllProducts failed:", e);
      const session = readStripeProductsSessionEntry(STRIPE_PRODUCTS_SESSION_MAX_AGE_MS);
      if (session?.data?.length) {
        stripeProductsMemory = session.data;
        stripeProductsMemoryTs = session.ts;
        return session.data;
      }
      if (Array.isArray(stripeProductsMemory) && stripeProductsMemory.length) {
        return stripeProductsMemory;
      }
      return [];
    } finally {
      if (stripeProductsInflight === p) {
        stripeProductsInflight = null;
      }
    }
  })();

  if (!force) {
    stripeProductsInflight = p;
  }
  return p;
}


export async function saveSuppliesIndustry(formDataArray, categoryArray) {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error("User is not authenticated.");
    }

    const currentUserId = currentUser.uid;
    const batch = writeBatch(firebase);

    const updatedCategories = categoryArray.map((category) => {
        const newCategoryRef = doc(collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY)); 
        const { spanish_title: _omitInitCatSt, spanish_description: _omitInitCatSd, ...catCore } = category;
        void _omitInitCatSt;
        void _omitInitCatSd;
        const categoryData = {
            ...catCore,
            id: newCategoryRef.id,
            industry: true,
            createdAt: serverTimestamp(),
            createdBy: currentUserId,
        };
        applyOptionalSpanishFirestoreFields(categoryData, category);

        batch.set(newCategoryRef, categoryData);
        return { ...category, id: newCategoryRef.id };
    });

    formDataArray.forEach((formData, index) => {
        const newSupplyRef = doc(collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY));
        const assignedCategory = updatedCategories[index % updatedCategories.length];
        const { spanish_title: _omitInitSupSt, spanish_description: _omitInitSupSd, ...supplyCore } = formData;
        void _omitInitSupSt;
        void _omitInitSupSd;
        const supplyData = {
            ...supplyCore,
            id: newSupplyRef.id,
            industry: true,
            parentCategoryId: assignedCategory.id,
            createdAt: serverTimestamp(),
            createdBy: currentUserId,
        };
        applyOptionalSpanishFirestoreFields(supplyData, formData);

        batch.set(newSupplyRef, supplyData);
    });

    try {
        await batch.commit();

        return { 
            status: true, 
            message: "All records saved successfully", 
            totalRecords: updatedCategories.length + formDataArray.length,
            categories: updatedCategories 
        };
    } catch (error) {
        console.error("Error saving data:", error);
        return { status: false, error: error.message };
    }
}

export async function getServicesDetails(companyId, onSuccess, onError) {
    try {
        const serviceDocRef = collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY);

        const q = query(serviceDocRef, 
            where("industry", "==", true), 
            where("companyId", "==", companyId)
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            onError && onError({ status: false, error: "No matching services found" });
            return { status: false, error: "No matching services found" };
        } else {
            const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            onSuccess && onSuccess({ status: true, data });
            return { status: true, data };
        }
    } catch (error) {
        onError && onError({ status: false, error: error.message });
        return { status: false, error: error.message };
    }
}

export async function saveOrUpdateSuppliesIndustry(formDataArray, categoryArray) {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
        throw new Error("User is not authenticated.");
    }

    const currentUserId = currentUser.uid;
    const batch = writeBatch(firebase);

    try {
        const categoryIds = categoryArray.map(cat => cat.id).filter(id => id);
        const supplyIds = formDataArray.map(sup => sup.id).filter(id => id);

        const existingCategories = await getExistingDocs(COLLECTIONS.SUPPLIES_COMPANY_CATEGORY, categoryIds);
        const existingSupplies = await getExistingDocs(COLLECTIONS.SUPPLIES_COMPANY_CATEGORY, supplyIds);

        const updatedCategories = [];
        const categoryMap = {};

        categoryArray.forEach(category => {
            let categoryRef;
            if (existingCategories[category.id]) {
                categoryRef = doc(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY, category.id);
                const { spanish_title: _omitCatSt, spanish_description: _omitCatSd, ...catCore } = category;
                void _omitCatSt;
                void _omitCatSd;
                const catPayload = {
                    ...catCore,
                    updatedAt: serverTimestamp(),
                    updatedBy: currentUserId
                };
                applyOptionalSpanishFirestoreFields(catPayload, category);
                batch.update(categoryRef, catPayload);
            } else {
                categoryRef = doc(collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY));
                const { spanish_title: _omitCatSt2, spanish_description: _omitCatSd2, ...catCoreNew } = category;
                void _omitCatSt2;
                void _omitCatSd2;
                const newCategory = { 
                    ...catCoreNew, 
                    id: categoryRef.id,
                    industry: true, 
                    createdAt: serverTimestamp(), 
                    createdBy: currentUserId 
                };
                applyOptionalSpanishFirestoreFields(newCategory, category);
                batch.set(categoryRef, newCategory);
            }
            categoryMap[category.id] = categoryRef.id;
            updatedCategories.push({ ...category, id: categoryRef.id });
        });

            formDataArray.forEach(formData => {
            const mappedParentId = categoryMap[formData.parentCategoryId] || formData.parentCategoryId;
            let supplyRef;

            if (existingSupplies[formData.id]) {
                supplyRef = doc(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY, formData.id);
                const { spanish_title: _omitSupSt, spanish_description: _omitSupSd, ...supplyCore } = formData;
                void _omitSupSt;
                void _omitSupSd;
                const supplyPayload = {
                    ...supplyCore,
                    parentCategoryId: mappedParentId,
                    updatedAt: serverTimestamp(),
                    updatedBy: currentUserId
                };
                applyOptionalSpanishFirestoreFields(supplyPayload, formData);
                batch.update(supplyRef, supplyPayload);
            } else {
                supplyRef = doc(collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY));
                const { spanish_title: _omitSupSt2, spanish_description: _omitSupSd2, ...supplyCoreNew } = formData;
                void _omitSupSt2;
                void _omitSupSd2;
                const newSupply = {
                    ...supplyCoreNew,
                    id: supplyRef.id,
                    industry: true,
                    parentCategoryId: mappedParentId,
                    createdAt: serverTimestamp(),
                    createdBy: currentUserId
                };
                applyOptionalSpanishFirestoreFields(newSupply, formData);
                batch.set(supplyRef, newSupply);
            }
        });

        await batch.commit();

        return { 
            status: true, 
            message: "Categories and supplies updated successfully", 
            totalRecords: updatedCategories.length + formDataArray.length,
            categories: updatedCategories 
        };
    } catch (error) {
        console.error("Error saving data:", error);
        return { status: false, error: error.message };
    }
}

/**
 * Helper function to get existing Firestore documents
 */
async function getExistingDocs(collectionPath, docIds) {
    if (docIds.length === 0) return {};

    const existingDocs = {};
    const chunkSize = 30;
    const docRef = collection(firebase, collectionPath);

    // Split into chunks of 30
    const chunks = [];
    for (let i = 0; i < docIds?.length; i += chunkSize) {
        chunks?.push(docIds?.slice(i, i + chunkSize));
    }

    const queries = chunks?.map(chunk => {
        const q = query(docRef, where("id", "in", chunk));
        return getDocs(q);
    });

    const results = await Promise.all(queries);

    results?.forEach(snapshot => {
        snapshot?.forEach(doc => {
            const data = doc.data();
            if (!data.deleted) {
                existingDocs[doc.id] = data;
            }
        });
    });

    return existingDocs;
}

export async function resetSuppliesIndustry(companyId, onSuccess, onError) {
    try {
        const colRef = collection(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY);
        const q = query(colRef, 
            where("industry", "==", true), 
            where("companyId", "==", companyId));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            onError && onError({ status: false, error: "No Settings found" });
            return { status: false, error: "No Settings found" };
        }

        const updatePromises = [];
        const updatedSettings = [];

        querySnapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data();
            if (!data.deleted) {
                const docRef = doc(firebase, COLLECTIONS.SUPPLIES_COMPANY_CATEGORY, docSnapshot.id);
                updatePromises.push(updateDoc(docRef, { deleted: true }));
                updatedSettings.push({ id: docSnapshot.id, ...data, deleted: true });
            }
        });

        await Promise.all(updatePromises);

        onSuccess && onSuccess({ status: true, data: updatedSettings });
        return { status: true, data: updatedSettings };

    } catch (error) {
        onError && onError({ status: false, error: error.message });
        return { status: false, error: error.message };
    }
}